import React, { createContext, useState, useEffect, useCallback, useContext } from "react";
import * as signalR from "@microsoft/signalr";
import { api_GET, api_PUT, urlApi } from "../services/api_service";
import { showMessage } from "../services/message";
import { getSessionCookie } from "../services/session";

export const AlertasContext = createContext();

export const AlertasProvider = ({ children }) => {
    const [alertas, setAlertas] = useState([]);
    const [permissaoExibirAlerta, setPermissaoExibirAlerta] = useState(false);
    const [permissaoCarregada, setPermissaoCarregada] = useState(false);

    const carregarAlertas = useCallback(async () => {
        const session = getSessionCookie();
        if (!session) return;
        try {
            const { data } = await api_GET("AvaliacaoColeta/GetAlertasInconformidades");
            setAlertas(data || []);
        } catch (error) {
            setAlertas([]);
            showMessage("Error", error?.message ?? "Erro ao carregar alertas", "error", null);
        }
    }, []);

    const permissaoExibirAlertasUsuario = useCallback(async () => {
        const session = getSessionCookie();
        if (!session) {
            setPermissaoExibirAlerta(false);
            setPermissaoCarregada(true);
            return;
        }
        try {
            const { data } = await api_GET(`usuario/GetPermissoesEspeciais/${session.usuarioId}`);
            const temPermissao =
                session.perfil === "Master" ||
                data.some((x) => x.permissao === "visualizar_alertas_os");

            setPermissaoExibirAlerta(temPermissao);
        } catch (error) {
            setPermissaoExibirAlerta(false);
            showMessage("Error", error?.message ?? "Erro ao verificar permissão", "error", null);
        } finally {
            setPermissaoCarregada(true);
        }
    }, []);

    const marcarVisualizado = useCallback(async (ordemColetaInconformidadeId) => {
        const session = getSessionCookie();
        if (!session) return;
        try {
            await api_PUT(
                `AvaliacaoColeta/UpdateVisualizacaoNotificacaoUsuarioByOrdemInconformidade/${ordemColetaInconformidadeId}`
            );
            setAlertas((prev) =>
                prev.filter((a) => a.ordemColetaInconformidadeId !== ordemColetaInconformidadeId)
            );
        } catch (error) {
            showMessage("Error", error?.message ?? "Erro ao marcar alerta", "error", null);
        }
    }, []);

    // Efeito para carregar permissões
    useEffect(() => {
        permissaoExibirAlertasUsuario();
    }, [permissaoExibirAlertasUsuario]);

    // Efeito para carregar alertas quando permissão carregada e permitida
    useEffect(() => {
        if (permissaoCarregada && permissaoExibirAlerta) {
            carregarAlertas();
        } else {
            setAlertas([]);
        }
    }, [permissaoCarregada, permissaoExibirAlerta, carregarAlertas]);

    // Efeito para SignalR
    useEffect(() => {
        if (!permissaoCarregada || !permissaoExibirAlerta) return;

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(`${urlApi.replace("/api/", "")}/ws`, { withCredentials: true })
            .build();

        connection.start()
            .then(() => console.log("Conectado ao SignalR"))
            .catch(err => console.error("Erro ao conectar SignalR:", err));

        connection.on("ReceberAlerta", carregarAlertas);

        return () => connection.stop();
    }, [permissaoCarregada, permissaoExibirAlerta, carregarAlertas]);

    return (
        <AlertasContext.Provider
            value={{
                alertas,
                setAlertas,
                carregarAlertas,
                marcarVisualizado,
                permissaoExibirAlerta,
                setPermissaoExibirAlerta,
                permissaoExibirAlertasUsuario
            }}
        >
            {children}
        </AlertasContext.Provider>
    );
};

export const useAlertas = () => useContext(AlertasContext);
